import axiosInstance from "utils/axios";
import { DocumentUploadFeature, uploadAttachments } from "./documentUploadService";

// Simple in-memory cache for getTemplates to prevent redundant slow fetches
const templatesCache = new Map();

export const clearTemplatesCache = () => {
  templatesCache.clear();
};

export const getTemplates = async function (params = {}) {
  // Support both object and positional parameter styles for backward compatibility
  let requestParams;
  if (typeof params === 'object' && params !== null) {
    // Object style: { pageNumber, pageSize, channelId, search }
    requestParams = {
      pageNumber: params.pageNumber || 1,
      pageSize: params.pageSize || 10,
      channelId: params.channelId || undefined, // Don't default to 1, let backend handle
      search: params.search || ""
    };
  } else {
    // Fallback for positional parameters (for SMS modal compatibility)
    requestParams = {
      pageNumber: arguments[0] || 1,
      pageSize: arguments[1] || 10,
      channelId: arguments[2] || 1,
      search: arguments[3] || ""
    };
  }

  const cacheKey = `${requestParams.channelId || 'all'}_${requestParams.pageNumber}_${requestParams.pageSize}_${requestParams.search}`;
  if (templatesCache.has(cacheKey)) {
    return templatesCache.get(cacheKey);
  }

  const fetchPromise = (async () => {
    try {
      const response = await axiosInstance.post('/api/Template/GetTemplates', requestParams);

      const responseData = response.data;

      // Check if the response indicates failure FIRST
      if (responseData?.status === "Failure" || responseData?.statusCode >= 400) {
        templatesCache.delete(cacheKey);
        return {
          success: false,
          error: responseData?.message || "Failed to fetch templates",
          data: [],
          totalRecords: 0,
        };
      }

      if (responseData.statusCode === 200 && responseData.status === "Success") {
        let templatesData = [];

        // Parse the data if it's a JSON string
        if (typeof responseData.data === "string") {
          try {
            templatesData = JSON.parse(responseData.data);
          } catch (parseError) {
            console.error("Error parsing templates data:", parseError);
            templatesData = [];
          }
        } else if (Array.isArray(responseData.data)) {
          templatesData = responseData.data;
        }

        // Normalize the template data structure for UI compatibility
        const normalizedData = templatesData.map(template => {
          return {
            // Keep original fields for backward compatibility first
            ...template,
            // Then override with normalized values
            id: template.TemplateId || template.id,
            name: template.Name || template.name,
            channelId: template.ChannelId || template.channelId,
            subject: template.Subject || template.subject || '',
            body: template.Body || template.body,
            mediaUrl: template.MediaUrl || template.mediaUrl || '',
            isActive: template.IsActive !== undefined ? template.IsActive : template.isActive,
            status: template.WhatsappStatus !== undefined ? template.WhatsappStatus : (template.Status !== undefined ? template.Status : template.status),
            // Force IsSystemTemplate to false to allow editing all templates
            IsSystemTemplate: template?.IsSystemTemplate,
            createdBy: template.CreatedBy || template.createdBy,
            createdDate: template.CreatedDate || template.createdDate,
            modifiedBy: template.ModifiedBy || template.modifiedBy,
            modifiedDate: template.ModifiedDate || template.modifiedDate,
            templateTypeId: template.TemplateTypeId || template.templateTypeId,
            attachments: template.Attachments || template.attachments || [],
          };
        });

        return {
          success: true,
          data: normalizedData,
          message: responseData.message || "Templates fetched successfully",
          totalRecords: responseData.totalRecords || normalizedData.length,
        };
      } else {
        throw new Error(responseData.message || "Failed to fetch templates");
      }
    } catch (error) {
      templatesCache.delete(cacheKey);
      console.error("Error fetching templates:", error);

      let errorMessage = "Failed to fetch templates";
      if (error.response) {
        const status = error.response.status;
        const serverMessage = error.response.data?.message || error.response.data?.error;
        if (status >= 400 && status < 500) {
          errorMessage = serverMessage || `Client error: ${status}`;
        } else if (status >= 500) {
          errorMessage = serverMessage || `Server error: ${status}`;
        }
      } else if (error.request) {
        errorMessage = "Network error: Unable to connect to server";
      } else {
        errorMessage = error.message || "Unknown error occurred";
      }

      return {
        success: false,
        error: errorMessage,
        statusCode: error.response?.status || null,
        data: [],
        totalRecords: 0,
      };
    }
  })();

  templatesCache.set(cacheKey, fetchPromise);
  return fetchPromise;
};

// Extract variables from template body (e.g., {{email}}, {{name}})
export const extractTemplateVariables = (templateBody) => {
  if (!templateBody) return [];

  // Remove HTML tags first
  const textContent = templateBody.replace(/<[^>]*>/g, '');

  // Find all variables in {{variable}} format
  const variableMatches = textContent.match(/\{\{([^}]+)\}\}/g);

  if (!variableMatches) return [];

  // Extract variable names and remove duplicates
  const variables = variableMatches.map(match => match.replace(/[{}]/g, ''));
  return [...new Set(variables)];
};

// Replace variables in template with actual values
export const replaceTemplateVariables = (templateBody, variables) => {
  if (!templateBody || !variables) return templateBody;

  let processedTemplate = templateBody;

  Object.keys(variables).forEach(key => {
    const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
    // If the variable has a value, use it. Otherwise, keep the original {{key}}
    const replacement = variables[key] ? variables[key] : `{{${key}}}`;
    processedTemplate = processedTemplate.replace(regex, replacement);
  });

  return processedTemplate;
};

// Convert HTML template to plain text for SMS
export const htmlToPlainText = (html) => {
  if (!html) return '';

  // Replace common HTML elements with text equivalents
  let text = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/h[1-6]>/gi, '\n')
    .replace(/<[^>]*>/g, '') // Remove all HTML tags
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  // Clean up extra whitespace and newlines
  text = text
    .replace(/\n\s*\n/g, '\n') // Remove empty lines
    .replace(/^\s+|\s+$/g, '') // Trim
    .replace(/\s+/g, ' '); // Replace multiple spaces with single space

  return text;
};

// Create a new template
// export const createTemplate = async (templateData) => {
//   try {
//     const response = await axiosInstance.post('/api/Template', templateData);

//     const responseData = response.data;

//     // Check if the response indicates failure FIRST
//     if (responseData?.status === "Failure" || responseData?.statusCode >= 400) {
//       throw new Error(responseData?.message || "Failed to create template");
//     }

//     // Check for success status
//     if (responseData.statusCode === 200 && responseData.status === "Success") {
//       return {
//         success: true,
//         data: responseData.data,
//         message: responseData.message || "Template created successfully",
//       };
//     }

//     // If we get here, return success with available data
//     return {
//       success: true,
//       data: responseData.data,
//       message: responseData.message || "Template created successfully",
//     };
//   } catch (error) {
//     console.error("Error creating template:", error);

//     let errorMessage = "Failed to create template";
//     if (error.response) {
//       const status = error.response.status;
//       const serverMessage = error.response.data?.message || error.response.data?.error;
//       if (status >= 400 && status < 500) {
//         errorMessage = serverMessage || `Client error: ${status}`;
//       } else if (status >= 500) {
//         errorMessage = serverMessage || `Server error: ${status}`;
//       }
//     } else if (error.request) {
//       errorMessage = "Network error: Unable to connect to server";
//     } else {
//       errorMessage = error.message || "Unknown error occurred";
//     }

//     throw new Error(errorMessage);
//   }
// };
export const createTemplate = async (templateData) => {
  try {
    // Pre-upload attachments via pre-signed S3 URLs (Initiate -> S3 Direct PUT -> Confirm)
    const filesToUpload = [];
    if (templateData.attachments && templateData.attachments.length > 0) {
      templateData.attachments.forEach((file) => {
        if (file instanceof File) {
          filesToUpload.push(file);
        }
      });
    }

    let preUploadedAttachments = [];
    if (filesToUpload.length > 0) {
      const uploadResult = await uploadAttachments(DocumentUploadFeature.TEMPLATE_ATTACHMENT, filesToUpload);
      if (!uploadResult.success) {
        throw new Error(uploadResult.message || "Failed to upload template attachments");
      }
      preUploadedAttachments = uploadResult.data;
    }

    const payload = {
      template: {
        name: templateData.name,
        channelId: Number(templateData.channelId),
        subject: templateData.subject || '',
        body: templateData.body || '',
        mediaUrl: templateData.mediaUrl || '',
        isActive: templateData.isActive !== undefined ? Boolean(templateData.isActive) : true,
        createdBy: templateData.createdBy,
        modifiedBy: templateData.modifiedBy,
        companyId: templateData.companyId,
        templateTypeId: templateData.templateTypeId,
        templateId: templateData.templateId
      },
      attachments: preUploadedAttachments.map((att, idx) => ({
        fileName: att.fileName,
        filePath: att.filePath,
        fileExtension: att.fileExtension,
        fileSizeBytes: filesToUpload[idx]?.size || (att.fileSizeKB ? att.fileSizeKB * 1024 : 0)
      }))
    };

    const response = await axiosInstance.post('/api/Template/Create-template', payload, {
      headers: { 'Content-Type': 'application/json' },
      timeout: 60000,
    });

    const responseData = response.data;

    if (responseData?.status === "Failure" || responseData?.statusCode >= 400) {
      throw new Error(responseData?.message || "Failed to create template");
    }

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      clearTemplatesCache();
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Template created successfully",
      };
    }

    clearTemplatesCache();
    return {
      success: true,
      data: responseData.data,
      message: responseData.message || "Template created successfully",
    };
  } catch (error) {
    console.error("Error creating template:", error);

    let errorMessage = "Failed to create template";
    if (error.response) {
      const status = error.response.status;
      const serverMessage = error.response.data?.message || error.response.data?.error;

      if (error.response.data?.errors) {
        const validationErrors = error.response.data.errors;
        const errorKeys = Object.keys(validationErrors);
        if (errorKeys.length > 0) {
          errorMessage = validationErrors[errorKeys[0]][0];
        }
      } else if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`;
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`;
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server";
    } else {
      errorMessage = error.message || "Unknown error occurred";
    }

    throw new Error(errorMessage);
  }
};

// Update an existing template
export const updateTemplate = async (templateId, templateData) => {
  try {
    // Process NEW file attachments - pre-upload via presigned S3 URLs
    const newFilesToUpload = [];
    if (templateData.attachments && templateData.attachments.length > 0) {
      templateData.attachments.forEach((file) => {
        if (file instanceof File) {
          newFilesToUpload.push(file);
        }
      });
    }

    let preUploadedNewFiles = [];
    if (newFilesToUpload.length > 0) {
      const uploadResult = await uploadAttachments(DocumentUploadFeature.TEMPLATE_ATTACHMENT, newFilesToUpload);
      if (!uploadResult.success) {
        throw new Error(uploadResult.message || "Failed to upload template attachments");
      }
      preUploadedNewFiles = uploadResult.data;
    }

    const attachmentsPayload = [];

    // 1. Process existing attachments - pass attachmentId so backend preserves them
    if (templateData.existingAttachments && templateData.existingAttachments.length > 0) {
      templateData.existingAttachments.forEach((attachment) => {
        const attachmentId = attachment.AttachmentId || attachment.attachmentId;
        if (attachmentId) {
          attachmentsPayload.push({
            attachmentId: attachmentId
          });
        }
      });
    }

    // 2. Process newly pre-uploaded S3 attachments
    preUploadedNewFiles.forEach((file, idx) => {
      const origFile = newFilesToUpload[idx];
      const bytes = origFile?.size || (file.fileSizeKB ? file.fileSizeKB * 1024 : 0);
      attachmentsPayload.push({
        fileName: file.fileName,
        filePath: file.filePath,
        fileExtension: file.fileExtension,
        fileSizeBytes: bytes
      });
    });

    const payload = {
      template: {
        templateId: templateId,
        name: templateData.name,
        channelId: Number(templateData.channelId),
        subject: templateData.subject || '',
        body: templateData.body || '',
        mediaUrl: templateData.mediaUrl || '',
        isActive: templateData.isActive !== undefined ? Boolean(templateData.isActive) : true,
        modifiedBy: templateData.modifiedBy,
        companyId: templateData.companyId,
        templateTypeId: templateData.templateTypeId,
        createdBy: templateData.createdBy
      },
      attachments: attachmentsPayload
    };

    const response = await axiosInstance.post('/api/Template/UpdateTemplate', payload, {
      headers: { 'Content-Type': 'application/json' },
      timeout: 60000,
    });

    const responseData = response.data;

    // Check if the response indicates failure FIRST
    if (responseData?.status === "Failure" || responseData?.statusCode >= 400) {
      throw new Error(responseData?.message || "Failed to update template");
    }

    // Check for success status
    if (responseData.statusCode === 200 && responseData.status === "Success") {
      clearTemplatesCache();
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Template updated successfully",
      };
    }

    // If we get here, return success with available data
    clearTemplatesCache();
    return {
      success: true,
      data: responseData.data,
      message: responseData.message || "Template updated successfully",
    };
  } catch (error) {
    console.error("Error updating template:", error);

    let errorMessage = "Failed to update template";
    if (error.response) {
      const status = error.response.status;
      const serverMessage = error.response.data?.message || error.response.data?.error;

      if (error.response.data?.errors) {
        const validationErrors = error.response.data.errors;
        const errorKeys = Object.keys(validationErrors);
        if (errorKeys.length > 0) {
          errorMessage = validationErrors[errorKeys[0]][0];
        }
      } else if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`;
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`;
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server";
    } else {
      errorMessage = error.message || "Unknown error occurred";
    }

    throw new Error(errorMessage);
  }
};

// Update template status only (optimized endpoint)
export const updateTemplateStatus = async (templateId, isActive) => {
  try {

    const payload = {
      templateId: String(templateId).trim(),
      isActive: Boolean(isActive)
    };


    const response = await axiosInstance.post('/api/Template/UpdateTemplateStatus', payload);

    const responseData = response.data;

    // Check for failure status
    if (responseData?.status === "Failure" || responseData?.statusCode >= 400) {
      throw new Error(responseData?.message || 'Failed to update template status');
    }

    // Check for success status
    if (responseData.statusCode === 200 && responseData.status === "Success") {
      clearTemplatesCache();
      return {
        success: true,
        message: responseData.message || 'Template status updated successfully',
        data: responseData.data
      };
    }

    // If we get here, return success with available data
    clearTemplatesCache();
    return {
      success: true,
      message: responseData.message || 'Template status updated successfully',
      data: responseData.data
    };
  } catch (error) {
    console.error('Error updating template status:', error);

    let errorMessage = "Failed to update template status";
    if (error.response) {
      const status = error.response.status;
      const serverMessage = error.response.data?.message || error.response.data?.error;
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`;
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`;
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server";
    } else {
      errorMessage = error.message || "Unknown error occurred";
    }

    throw new Error(errorMessage);
  }
};

// Delete a template
export const deleteTemplate = async (templateId) => {
  try {
    const response = await axiosInstance.post(`/api/Template/${templateId}`);

    const responseData = response.data;

    // Check if the response indicates failure FIRST
    if (responseData?.status === "Failure" || responseData?.statusCode >= 400) {
      throw new Error(responseData?.message || "Failed to delete template");
    }

    // Check for success status
    if (responseData.statusCode === 200 && responseData.status === "Success") {
      clearTemplatesCache();
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Template deleted successfully",
      };
    }

    // If we get here, return success with available data
    clearTemplatesCache();
    return {
      success: true,
      data: responseData.data,
      message: responseData.message || "Template deleted successfully",
    };
  } catch (error) {
    console.error("Error deleting template:", error);

    let errorMessage = "Failed to delete template";
    if (error.response) {
      const status = error.response.status;
      const serverMessage = error.response.data?.message || error.response.data?.error;
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`;
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`;
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server";
    } else {
      errorMessage = error.message || "Unknown error occurred";
    }

    throw new Error(errorMessage);
  }
};